import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Power } from 'lucide-react';
import { getWidgetConfig } from './widgetConfig';
import GaugeWidget from './GaugeWidget';
import ThermometerWidget from './ThermometerWidget';
import StateWidget from './StateWidget';
import UptimeWidget from './UptimeWidget';

const WIDGET_COMPONENTS = {
  gauge: GaugeWidget,
  thermometer: ThermometerWidget,
  state: StateWidget,
  uptime: UptimeWidget,
};

function isActiveValue(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'on' || v === '1' || v === 'true' || v === 'activé';
  }
  return false;
}

export default function NodeCard({ noeud, onToggle }) {
  const { t } = useTranslation();
  const { sn, type, categorie, derniereValeur, unite, etat, _id } = noeud;
  const [toggling, setToggling] = useState(false);

  const config = useMemo(() => getWidgetConfig(type, categorie), [type, categorie]);

  const isOnline = etat === 'En ligne';
  const isActuator = categorie === 'Actionneur';
  const isActive = isActiveValue(derniereValeur);

  const WidgetComponent = WIDGET_COMPONENTS[config.component] || GaugeWidget;

  // Build props specific to each widget type
  const widgetProps = useMemo(() => {
    switch (config.component) {
      case 'state':
        return {
          value: derniereValeur,
          label: type,
        };
      case 'uptime':
        return {
          value: typeof derniereValeur === 'number' ? derniereValeur : parseFloat(derniereValeur) || 0,
        };
      case 'thermometer':
        return {
          value: typeof derniereValeur === 'number' ? derniereValeur : parseFloat(derniereValeur) || 0,
          unit: unite || config.unit,
          min: config.min,
          max: config.max,
        };
      case 'gauge':
      default:
        return {
          value: typeof derniereValeur === 'number' ? derniereValeur : parseFloat(derniereValeur) || 0,
          unit: unite || config.unit,
          min: config.min,
          max: config.max,
          color: config.color,
          thresholds: config.thresholds,
        };
    }
  }, [config, derniereValeur, unite, type]);

  // Determine the display value + unit for the footer
  // Hors ligne : la dernière valeur n'est plus représentative — afficher "État inconnu"
  const footerValue = useMemo(() => {
    if (config.component === 'state') {
      return null; // StateWidget already shows its label
    }
    if (!isOnline) {
      return t('common.unknownState', 'État inconnu');
    }
    const v = typeof derniereValeur === 'number'
      ? (Number.isInteger(derniereValeur) ? derniereValeur : derniereValeur.toFixed(1))
      : derniereValeur ?? '--';
    return `${v} ${unite || config.unit}`.trim();
  }, [config, derniereValeur, unite, isOnline, t]);

  const handleToggle = async () => {
    if (!onToggle || toggling) return;
    setToggling(true);
    try {
      await onToggle(_id, sn, !isActive);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 flex flex-col items-center gap-2 w-48 hover:shadow-md transition-shadow duration-200">
      {/* Header: SN + type + status dot */}
      <div className="w-full flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{sn}</p>
          <p className="text-[10px] text-gray-400 truncate">{type}</p>
        </div>
        <span
          className={`ml-2 flex-shrink-0 w-2.5 h-2.5 rounded-full ${
            isOnline ? 'bg-green-400' : 'bg-gray-300'
          }`}
          title={isOnline ? t('common.online') : t('common.offline')}
        />
      </div>

      {/* Widget */}
      <div className="flex items-center justify-center py-1">
        <WidgetComponent {...widgetProps} />
      </div>

      {/* Footer value */}
      {footerValue && (
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{footerValue}</p>
      )}

      {/* Toggle button for actuators */}
      {isActuator && onToggle && (
        <button
          onClick={handleToggle}
          disabled={toggling || !isOnline}
          className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            isActive
              ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/50'
              : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/50'
          }`}
        >
          <Power size={12} />
          {toggling
            ? t('common.loading')
            : isActive
              ? t('common.deactivate')
              : t('common.activate')
          }
        </button>
      )}
    </div>
  );
}
